import { useEffect, useId, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, Loader2, XIcon } from 'lucide-react';

import { SoftImage } from '@/components/site/soft-image';
import { filePreviewUrl } from '@/lib/media-url';
import { cn } from '@/lib/utils';

export type LightboxSlide = {
    /** Mid-size / inline preview (fallback). */
    src: string;
    /** Storage file id - used to load a larger enlarge preview. */
    fileId?: string;
    alt: string;
    caption?: string;
    credit?: string;
};

function enlargeSrc(slide: LightboxSlide): string {
    if (slide.fileId) {
        return (
            filePreviewUrl(slide.fileId, {
                width: 1600,
                quality: 80,
                output: 'avif',
            }) ?? slide.src
        );
    }
    return slide.src;
}

/**
 * Full-viewport lightbox for article body images / galleries.
 * Supports Escape (via Dialog), click-outside close, and arrow keys when
 * more than one slide is provided.
 */
export function ImageLightbox({
    open,
    onOpenChange,
    slides,
    index,
    onIndexChange,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    slides: LightboxSlide[];
    index: number;
    onIndexChange?: (index: number) => void;
}) {
    const titleId = useId();
    const safeIndex =
        slides.length === 0
            ? 0
            : Math.min(Math.max(index, 0), slides.length - 1);
    const slide = slides[safeIndex];
    const multi = slides.length > 1;
    const src = slide ? enlargeSrc(slide) : '';
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!open || !src) {
            setLoaded(false);
            return;
        }
        setLoaded(false);
        let cancelled = false;
        const img = new Image();
        const mark = () => {
            if (!cancelled) setLoaded(true);
        };
        img.onload = mark;
        img.onerror = mark;
        img.src = src;
        if (img.complete) mark();
        return () => {
            cancelled = true;
            img.onload = null;
            img.onerror = null;
        };
    }, [src, open]);

    useEffect(() => {
        if (!open || !multi || !onIndexChange) return;

        function onKey(e: KeyboardEvent) {
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                // RTL reading: ArrowRight = previous in gallery order.
                onIndexChange!(
                    (safeIndex - 1 + slides.length) % slides.length,
                );
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                onIndexChange!((safeIndex + 1) % slides.length);
            }
        }

        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, multi, onIndexChange, safeIndex, slides.length]);

    if (!slide || !open) return null;

    const captionLine = [slide.caption, slide.credit]
        .filter(Boolean)
        .join(' · ');
    const showLoader = !loaded;

    return (
        <DialogPrimitive.Root open onOpenChange={onOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay
                    className={cn(
                        'fixed inset-0 z-50 bg-black/90',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out',
                        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                    )}
                />
                <DialogPrimitive.Content
                    dir="rtl"
                    aria-labelledby={titleId}
                    className={cn(
                        'fixed inset-0 z-50 flex flex-col outline-none',
                        'data-[state=open]:animate-in data-[state=closed]:animate-out',
                        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                    )}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <DialogPrimitive.Title id={titleId} className="sr-only">
                        {slide.alt || 'תצוגה מוגדלת'}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="sr-only">
                        {captionLine || 'לחצו Escape או על הרקע לסגירה'}
                    </DialogPrimitive.Description>

                    <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-3 md:px-6">
                        <p className="min-w-0 truncate text-sm text-white/55">
                            {multi
                                ? `${safeIndex + 1} / ${slides.length}`
                                : '\u00a0'}
                        </p>
                        <DialogPrimitive.Close
                            className="inline-flex size-10 items-center justify-center text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                            aria-label="סגור"
                        >
                            <XIcon className="size-5" />
                        </DialogPrimitive.Close>
                    </div>

                    {/* Image fills remaining height; caption stays pinned below it. */}
                    <div
                        className="relative flex min-h-0 flex-1 flex-col"
                        onClick={() => onOpenChange(false)}
                    >
                        {multi ? (
                            <>
                                <button
                                    type="button"
                                    className="absolute start-2 top-1/2 z-10 inline-flex size-11 -translate-y-1/2 items-center justify-center text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 md:start-4"
                                    aria-label="תמונה קודמת"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onIndexChange?.(
                                            (safeIndex - 1 + slides.length) %
                                                slides.length,
                                        );
                                    }}
                                >
                                    <ChevronRight className="size-7" />
                                </button>
                                <button
                                    type="button"
                                    className="absolute end-2 top-1/2 z-10 inline-flex size-11 -translate-y-1/2 items-center justify-center text-white/70 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 md:end-4"
                                    aria-label="תמונה הבאה"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onIndexChange?.(
                                            (safeIndex + 1) % slides.length,
                                        );
                                    }}
                                >
                                    <ChevronLeft className="size-7" />
                                </button>
                            </>
                        ) : null}

                        <div
                            className="relative flex min-h-0 flex-1 items-center justify-center px-4 md:px-16"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="relative flex h-full max-h-full w-full max-w-5xl items-center justify-center">
                                {/* Mid-res stand-in keeps frame size stable while the enlarge URL loads. */}
                                <img
                                    src={slide.src}
                                    alt=""
                                    aria-hidden
                                    className={cn(
                                        'max-h-full max-w-full object-contain',
                                        loaded ? 'invisible' : 'opacity-40',
                                    )}
                                    decoding="async"
                                />
                                <SoftImage
                                    key={src}
                                    src={src}
                                    alt={slide.alt}
                                    className="absolute inset-0 m-auto max-h-full max-w-full object-contain"
                                    decoding="async"
                                />
                                {showLoader ? (
                                    <div
                                        className="absolute inset-0 flex items-center justify-center"
                                        aria-hidden
                                    >
                                        <Loader2
                                            className="size-9 animate-spin text-white/75"
                                            strokeWidth={1.75}
                                        />
                                        <span className="sr-only">טוען תמונה</span>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        {/* Reserved footer so captions are never covered by tall images. */}
                        <div
                            className="shrink-0 border-t border-white/10 px-4 py-3 md:px-6"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <p
                                className="mx-auto min-h-[2.75rem] max-w-2xl text-center text-sm leading-relaxed text-white/60"
                                aria-hidden={!captionLine}
                            >
                                {captionLine || '\u00a0'}
                            </p>
                        </div>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
